import { useState, type ReactNode } from 'react';
import { NJButton, NJIconButton } from '@engie-group/fluid-design-system-react';
import { UMIcon, UMR } from '../../urbanmap/_components';
import { PLAN } from '../proposition-c/tokens';
import { PlanLogo, fmt } from '../proposition-c/ui';
import type { PlanProject } from './types';
import { AvatarStack } from './ui';

const KIND_ICON: Record<string, string> = { commune: 'location_city', communes: 'account_balance', zone: 'draw' };

/** Horizontal card row of the Nemo home page (title + optional action + scrollable cards). */
function CardRow({ title, action, children }: { title: string; action?: ReactNode; children: ReactNode }) {
  return (
    <section style={{ margin: '0 0 52px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: action ? 42 : 40, marginBottom: action ? 18 : 0 }}>
        <h4 style={{ margin: 0, fontWeight: 400, fontSize: 24, lineHeight: '40px' }}>{title}</h4>
        {action}
      </div>
      <div style={{ display: 'flex', gap: 20, overflowX: 'auto', paddingBottom: 4 }}>{children}</div>
    </section>
  );
}

function ProjectCard({
  project, onOpen, favorite, onToggleFavorite,
}: {
  project: PlanProject;
  onOpen: () => void;
  favorite: boolean;
  onToggleFavorite: () => void;
}) {
  return (
    <div className="nj-card" style={{ width: 260, minWidth: 260, height: 205, flexShrink: 0, overflow: 'hidden' }}>
      <div className="nj-card__body" style={{ padding: '12px 20px', display: 'flex', flexDirection: 'column', height: '100%', boxSizing: 'border-box', minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, height: 46, minHeight: 46, flexShrink: 0 }}>
          <UMIcon name={KIND_ICON[project.territoryKind] ?? 'map'} size={20} color={PLAN.accent} style={{ marginTop: 6 }} />
          <a
            href="#"
            title={project.name}
            style={{
              flex: 1, minWidth: 0, fontSize: '0.8rem', lineHeight: '19px', padding: '4px 0', color: UMR.text, textDecoration: 'none',
              overflowWrap: 'anywhere', display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2, overflow: 'hidden',
            }}
            onClick={(event) => {
              event.preventDefault();
              onOpen();
            }}
          >
            {project.name}
          </a>
          <NJIconButton
            icon={favorite ? 'close' : 'favorite_border'}
            label={favorite ? 'Retirer des favoris' : 'Ajouter aux favoris'}
            variant="tertiary"
            scale="xs"
            onClick={onToggleFavorite}
          />
        </div>
        {/* Plan shows a thumbnail of the frozen perimeter here. */}
        <div
          aria-hidden
          style={{
            height: 60, minHeight: 60, flexShrink: 0, boxSizing: 'border-box',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: UMR.grey100, color: UMR.textSecondary, fontSize: 12, textAlign: 'center', padding: '0 8px',
            overflow: 'hidden', minWidth: 0,
          }}
        >
          <span style={{ minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{project.perimeterLabel}</span>
        </div>
        <div style={{ marginTop: 6, flexShrink: 0, fontSize: 12, lineHeight: '16px', color: UMR.textSecondary }}>
          {fmt(project.buildingCount)} bâtiments
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 'auto', flexShrink: 0, minWidth: 0 }}>
          <span style={{ flex: 1, minWidth: 0, fontSize: 12.8, lineHeight: '16px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: UMR.textSecondary }}>
            créé le {project.createdAt}
          </span>
          <AvatarStack members={project.members} max={3} />
        </div>
      </div>
    </div>
  );
}

/** PRD US-005 — the user only sees the projects they are a member of. */
export function ProjectsList({
  projects, onOpen, onCreate, canCreate, onBackToUM,
}: {
  projects: PlanProject[];
  onOpen: (p: PlanProject) => void;
  onCreate: () => void;
  canCreate: boolean;
  onBackToUM: () => void;
}) {
  const [favorites, setFavorites] = useState<string[]>(() => projects.slice(0, 1).map((p) => p.id));

  const toggleFavorite = (id: string) =>
    setFavorites((list) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]));

  const favoriteProjects = projects.filter((p) => favorites.includes(p.id));

  const card = (p: PlanProject) => (
    <ProjectCard
      key={p.id}
      project={p}
      favorite={favorites.includes(p.id)}
      onOpen={() => onOpen(p)}
      onToggleFavorite={() => toggleFavorite(p.id)}
    />
  );

  return (
    <div style={{ height: '100%', overflowY: 'auto', background: UMR.white, fontFamily: UMR.font }}>
      <div style={{ maxWidth: 1360, margin: '0 auto', padding: '28px 24px 40px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28 }}>
          <PlanLogo size={40} />
          <div style={{ flex: 1 }}>
            <h1 style={{ margin: 0, fontSize: 24, lineHeight: '32px', fontWeight: 700 }}>Mes projets Plan</h1>
            <div style={{ fontSize: 13, color: UMR.textSecondary }}>
              Vous ne voyez que les projets dont vous êtes membre.
            </div>
          </div>
          <NJButton label="UrbanMap" variant="secondary" emphasis="subtle" icon="arrow_back" onClick={onBackToUM} />
        </div>

        {projects.length === 0 ? (
          <div style={{ background: UMR.white, border: `1px solid ${UMR.border}`, padding: 32, textAlign: 'center', color: UMR.textSecondary }}>
            <UMIcon name="folder_off" size={32} color={UMR.textSecondary} />
            <div style={{ marginTop: 8, fontSize: 14 }}>Aucun projet pour l’instant. Créez-en un depuis un périmètre UrbanMap.</div>
            {canCreate && <div style={{ marginTop: 16 }}><NJButton label="Nouveau projet" onClick={onCreate} /></div>}
          </div>
        ) : (
          <>
            <CardRow title={`Mes projets favoris (${favoriteProjects.length})`}>
              {favoriteProjects.length > 0 ? (
                favoriteProjects.map(card)
              ) : (
                <div style={{ fontSize: 13, color: UMR.textSecondary, padding: '8px 0' }}>
                  Aucun projet en favori — utilisez le cœur sur une carte.
                </div>
              )}
            </CardRow>

            <CardRow
              title={`Mes derniers projets créés (${projects.length})`}
              action={canCreate ? <NJButton label="Nouveau projet" onClick={onCreate} /> : undefined}
            >
              {projects.map(card)}
            </CardRow>
          </>
        )}
      </div>
    </div>
  );
}
